/*
  announcement-bar.js

  Progressive enhancement only. The bar is server-rendered and readable with
  this file blocked; all this adds is the dismiss interaction and, where the
  merchant has written more than one message, the rotation.

  It deliberately does NOT handle the "already dismissed" case — that runs from
  a small blocking script in the section itself, because doing it here (deferred)
  would paint the bar and then remove it, which is exactly the layout shift the
  performance budget forbids (CLAUDE.md section 10).

  ── The rotation moves a class and nothing else ─────────────────────────────
  Every message is already in the DOM, stacked in one grid cell, with the first
  carrying the active class from the server. So this file never writes text,
  never measures anything, and never touches a layout property — it advances an
  index. The resting state with this file blocked is "message one", which is a
  complete state rather than a degraded one.

  ── Every guard resolves toward NOT moving ──────────────────────────────────
    reduced motion   no rotation at all. Swapping text under someone who asked
                     for less motion is the thing they asked not to have —
                     removing only the fade would leave the text still jumping.
    hover or focus   paused. Someone reading or reaching for the link should not
                     have it change under them.
    tab hidden       paused, so a backgrounded tab is not burning timers.
    fewer than two   nothing runs.

  The interval has a FLOOR here rather than a low option in the schema, on the
  same reasoning lead-capture.js records: a default is something a merchant can
  lower and a floor is not. A message nobody can finish reading is worse than
  having only one.
*/

class RnsAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.setUpDismiss();
    this.setUpRotation();
  }

  disconnectedCallback() {
    this.stop();
    if (this.onVisibility) {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  setUpDismiss() {
    const button = this.querySelector('[data-rns-announcement-dismiss]');
    if (!button) return;

    button.addEventListener('click', () => {
      this.hidden = true;
      this.stop();

      // Private browsing and blocked storage both throw here. A dismissal that
      // does not persist is a far better outcome than a broken bar.
      try {
        const key = this.dataset.rnsStorageKey;
        if (key) localStorage.setItem(key, '1');
      } catch (error) {
        /* no-op */
      }
    });
  }

  setUpRotation() {
    // Absent unless the section decided the bar may rotate — which it only does
    // when there is more than one message AND the bar can be dismissed. That
    // second condition is WCAG 2.2.2's "pause, stop or hide", and it is enforced
    // in Liquid so no setting combination can defeat it.
    const seconds = parseInt(this.dataset.rnsRotate, 10);
    if (!seconds) return;

    this.messages = this.querySelectorAll('[data-rns-announcement-message]');
    if (this.messages.length < 2) return;

    let reduced = false;
    try {
      reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (error) {
      // An unusable matchMedia is treated as "reduce". The safe answer to "does
      // this person want movement?" is no.
      reduced = true;
    }
    if (reduced) return;

    this.index = 0;
    this.interval = Math.max(seconds, 5) * 1000;

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    this.onVisibility = () => {
      if (document.hidden) this.stop();
      else this.start();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.start();
  }

  start() {
    if (this.timer || !this.messages || this.hidden) return;
    this.timer = window.setInterval(() => this.advance(), this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  advance() {
    const active = 'rns-announcement-bar__message--active';
    this.messages[this.index].classList.remove(active);
    this.index = (this.index + 1) % this.messages.length;
    this.messages[this.index].classList.add(active);
  }
}

if (!customElements.get('rns-announcement-bar')) {
  customElements.define('rns-announcement-bar', RnsAnnouncementBar);
}
